import React, { useState, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import WorkflowSidebar from './components/WorkflowSidebar';
import MethodCard from './components/MethodCard';
import CancelButton from './components/CancelButton';
import apiClient from '../../api/client';
import { useJobStore } from '../../stores/jobStore';
import SaveResultsWizard from '../../components/chemistry/SaveResultsWizard';
import { useWorkbenchContext } from '../../hooks/useWorkbenchContext';
import WorkbenchContextBar from '../../components/workbench/WorkbenchContextBar';
import ToolSwitcher from '../../components/workbench/ToolSwitcher';
import { useToolStep } from '../../hooks/useWorkbenchContext';

const STEPS = [
  { name: 'Target' },
  { name: 'Ligand' },
  { name: 'Configure' },
  { name: 'Dock' },
  { name: 'Results' },
];

const WorkbenchDocking: React.FC = () => {
  const { smiles: ctxSmiles, moleculeId: ctxMoleculeId, fileId: ctxFileId, projectId: ctxProjectId } = useWorkbenchContext();
  const [step, setStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [receptorText, setReceptorText] = useState('');
  const [receptorFormat, setReceptorFormat] = useState('pdb');
  const [ligandText, setLigandText] = useState(ctxSmiles || '');
  const [ligandFormat, setLigandFormat] = useState('smi');
  const [exhaustiveness, setExhaustiveness] = useState(8);
  const [numModes, setNumModes] = useState(9);
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [jobSubmitted, setJobSubmitted] = useState(false);
  const [deletedPoseIndices, setDeletedPoseIndices] = useState<Set<number>>(new Set());
  const [selectedPoses, setSelectedPoses] = useState<Set<number>>(new Set());
  const abortRef = useRef<AbortController | null>(null);
  const { submitJob } = useJobStore();
  const [saveOpen, setSaveOpen] = useState(false);
  const { bailOutText, setHasSaved } = useToolStep();

  const activePoses = useMemo(() => {
    if (!result?.poses) return [];
    return (result.poses as any[])
      .map((pose: any, i: number) => ({ pose, idx: i }))
      .filter(({ idx }: { idx: number }) => !deletedPoseIndices.has(idx));
  }, [result, deletedPoseIndices]);

  const handleReceptorFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'pdbqt') setReceptorFormat('pdbqt');
    else setReceptorFormat('pdb');
    file.text().then(setReceptorText);
  };

  const handleLigandFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'sdf' || ext === 'mol') setLigandFormat(ext);
    else if (ext === 'pdbqt') setLigandFormat('pdbqt');
    else setLigandFormat('smi');
    file.text().then(setLigandText);
  };

  const runDocking = async () => {
    setIsRunning(true);
    setError('');
    setResult(null);
    setDeletedPoseIndices(new Set());
    setSelectedPoses(new Set());
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await apiClient.post('/v2/docking/dock', {
        receptor_text: receptorText,
        ligand_text: ligandText,
        receptor_format: receptorFormat,
        ligand_format: ligandFormat,
        exhaustiveness,
        num_modes: numModes,
      }, { signal: controller.signal });
      const data = resp.data;
      setResult(data);
      setCompletedSteps((prev) => Array.from(new Set([...prev, 3])));
      setStep(4);
    } catch (err: any) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') setError('Docking cancelled by user');
      else setError(err.response?.data?.detail || err.message || 'Docking failed');
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  };

  return (
    <>
    <WorkbenchContextBar />
    <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
      <WorkflowSidebar steps={STEPS} currentStep={step} completedSteps={completedSteps} onStepClick={setStep} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={statusBarStyle}>
          <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <ToolSwitcher currentToolId="docking" />
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Engine: AutoDock Vina</span>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Step {step + 1}/{STEPS.length}</span>
          <div style={{ flex: 1 }} />
          <CancelButton onCancel={() => abortRef.current?.abort()} isRunning={isRunning} label="Stop Docking" />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={warningBannerStyle}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Limitations: AutoDock Vina may produce unreliable results for:</div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                <li>Flexible receptor docking</li>
                <li>Metal-coordinated binding sites</li>
                <li>Systems with large entropic contributions (e.g. water displacement)</li>
                <li>Induced-fit binding mechanisms</li>
              </ul>
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>For these cases, consider specialised tools or consult a computational chemist.</div>
            </div>

            {step === 0 && (
              <div>
                <h2 style={h2Style}>Upload Target</h2>
                <p style={descStyle}>PDB or PDBQT format for your protein target. Upload a file or paste the structure text.</p>
                <label style={{ ...btnStyle, cursor: 'pointer', marginBottom: 12, display: 'inline-flex' }}>
                  Upload PDB/PDBQT
                  <input type="file" accept=".pdb,.pdbqt" style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleReceptorFile(f); }} />
                </label>
                <textarea value={receptorText} onChange={(e) => setReceptorText(e.target.value)}
                  placeholder="Paste PDB text here..." style={textareaStyle} rows={12} />
                <div style={{ marginTop: 8 }}>
                  <label style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    Format:{' '}
                    <select value={receptorFormat} onChange={(e) => setReceptorFormat(e.target.value)} style={selectStyle}>
                      <option value="pdb">PDB</option>
                      <option value="pdbqt">PDBQT</option>
                    </select>
                  </label>
                </div>
                <button onClick={() => { if (receptorText.trim()) { setCompletedSteps((prev) => Array.from(new Set([...prev, 0]))); setStep(1); } }}
                  disabled={!receptorText.trim()} style={{ ...primaryBtnStyle, marginTop: 12, opacity: receptorText.trim() ? 1 : 0.5 }}>Continue</button>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 style={h2Style}>Upload or Enter Ligand</h2>
                <p style={descStyle}>Enter molecule notation, or upload SDF/MOL/PDBQT file.</p>
                <label style={{ ...btnStyle, cursor: 'pointer', marginBottom: 12, display: 'inline-flex' }}>
                  Upload Ligand File
                  <input type="file" accept=".smi,.sdf,.mol,.pdbqt" style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLigandFile(f); }} />
                </label>
                <textarea value={ligandText} onChange={(e) => setLigandText(e.target.value)}
                  placeholder="Enter molecule notation or paste structure..." style={textareaStyle} rows={6} />
                <div style={{ marginTop: 8 }}>
                  <label style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    Format:{' '}
                    <select value={ligandFormat} onChange={(e) => setLigandFormat(e.target.value)} style={selectStyle}>
                      <option value="smi">SMILES</option>
                      <option value="sdf">SDF</option>
                      <option value="mol">MOL</option>
                      <option value="pdbqt">PDBQT</option>
                    </select>
                  </label>
                </div>
                <button onClick={() => { if (ligandText.trim()) { setCompletedSteps((prev) => Array.from(new Set([...prev, 1]))); setStep(2); } }}
                  disabled={!ligandText.trim()} style={{ ...primaryBtnStyle, marginTop: 12, opacity: ligandText.trim() ? 1 : 0.5 }}>Continue</button>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 style={h2Style}>Docking Configuration</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 400 }}>
                  <div>
                    <label style={{ color: 'var(--text-muted)', fontSize: 13, display: 'block', marginBottom: 6 }}>
                      Exhaustiveness: <span style={{ color: '#f59e0b', fontWeight: 600 }}>{exhaustiveness}</span>
                    </label>
                    <input type="range" min={1} max={32} value={exhaustiveness}
                      onChange={(e) => setExhaustiveness(parseInt(e.target.value))}
                      style={{ width: '100%', accentColor: '#f59e0b' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)', fontSize: 11 }}>
                      <span>Fast (1)</span><span>Thorough (32)</span>
                    </div>
                  </div>
                  <div>
                    <label style={{ color: 'var(--text-muted)', fontSize: 13, display: 'block', marginBottom: 6 }}>
                      Number of modes: <span style={{ color: '#f59e0b', fontWeight: 600 }}>{numModes}</span>
                    </label>
                    <input type="range" min={1} max={20} value={numModes}
                      onChange={(e) => setNumModes(parseInt(e.target.value))}
                      style={{ width: '100%', accentColor: '#f59e0b' }} />
                  </div>
                </div>
                <button onClick={() => { setCompletedSteps((prev) => Array.from(new Set([...prev, 2]))); setStep(3); }}
                  style={{ ...primaryBtnStyle, marginTop: 20 }}>Continue to Docking</button>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 style={h2Style}>Run Docking</h2>
                <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ color: '#fbbf24', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Docking typically takes 30 seconds to 2 minutes</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Exhaustiveness: {exhaustiveness} | Modes: {numModes}</div>
                </div>
                {!isRunning && !result && !jobSubmitted && (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button onClick={runDocking} style={{ ...primaryBtnStyle, fontSize: 15, padding: '12px 24px' }}>Run Docking</button>
                    <button onClick={async () => {
                      try {
                        await submitJob('docking', {
                          receptor_text: receptorText, ligand_text: ligandText,
                          receptor_format: receptorFormat, ligand_format: ligandFormat,
                          exhaustiveness, num_modes: numModes,
                        });
                        setJobSubmitted(true);
                      } catch (err: any) { setError(err.message || 'Failed to submit job'); }
                    }} style={{ ...btnStyle, fontSize: 15, padding: '12px 24px' }}>Submit as Background Job</button>
                  </div>
                )}
                {jobSubmitted && (
                  <div style={{ background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.3)', borderRadius: 10, padding: '14px 16px' }}>
                    <div style={{ color: '#16a34a', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Job submitted — you can continue working</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Check the bell icon in the header for progress. Results will appear when complete.</div>
                  </div>
                )}
                {isRunning && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={spinnerStyle} />
                    <span style={{ color: '#f59e0b', fontSize: 14 }}>Docking in progress...</span>
                  </div>
                )}
                {error && <div style={errorStyle}>{error}</div>}
              </div>
            )}

            {step === 4 && result && (
              <div>
                <h2 style={h2Style}>Docking Results ({activePoses.length} poses)</h2>
                {activePoses.length > 0 ? (
                  <>
                    {/* Toolbar: Select All / Deselect All / Delete Selected */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                      <button
                        onClick={() => setSelectedPoses(new Set(activePoses.map(({ idx }) => idx)))}
                        style={{ ...btnStyle, fontSize: 12, padding: '5px 10px' }}
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => setSelectedPoses(new Set())}
                        style={{ ...btnStyle, fontSize: 12, padding: '5px 10px' }}
                      >
                        Deselect All
                      </button>
                      {selectedPoses.size > 0 && (
                        <button
                          onClick={() => {
                            setDeletedPoseIndices((prev) => {
                              const next = new Set(prev);
                              selectedPoses.forEach((i) => next.add(i));
                              return next;
                            });
                            setSelectedPoses(new Set());
                          }}
                          style={{ ...btnStyle, fontSize: 12, padding: '5px 10px', color: '#fb7185', borderColor: 'rgba(239,68,68,0.4)' }}
                        >
                          Delete Selected ({selectedPoses.size})
                        </button>
                      )}
                      <span style={{ color: 'var(--text-secondary)', fontSize: 12, marginLeft: 4 }}>
                        {selectedPoses.size > 0 ? `${selectedPoses.size} selected` : ''}
                      </span>
                    </div>

                    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                            <th style={{ ...thStyle, width: 36 }}></th>
                            <th style={thStyle}>ID</th>
                            <th style={thStyle}>Affinity (kcal/mol)</th>
                            <th style={{ ...thStyle, width: 36 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {activePoses.map(({ pose, idx }, displayIdx) => (
                            <tr key={idx} style={{
                              borderBottom: '1px solid var(--card-bg)',
                              background: selectedPoses.has(idx) ? 'rgba(245,158,11,0.08)' : undefined,
                              transition: 'background 0.15s',
                            }}>
                              <td style={{ ...tdStyle, width: 36, textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedPoses.has(idx)}
                                  onChange={() => setSelectedPoses((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(idx)) next.delete(idx); else next.add(idx);
                                    return next;
                                  })}
                                  style={{ cursor: 'pointer', accentColor: '#f59e0b' }}
                                />
                              </td>
                              <td style={{ ...tdStyle, fontWeight: 600, color: '#f59e0b' }}>
                                DOCK-{String(displayIdx + 1).padStart(3, '0')}
                              </td>
                              <td style={{ ...tdStyle, color: '#f59e0b', fontWeight: 600 }}>
                                {pose.affinity != null ? pose.affinity.toFixed(2) : 'N/A'}
                              </td>
                              <td style={{ ...tdStyle, width: 36, textAlign: 'center' }}>
                                <button
                                  onClick={() => {
                                    setDeletedPoseIndices((prev) => new Set(prev).add(idx));
                                    setSelectedPoses((prev) => { const next = new Set(prev); next.delete(idx); return next; });
                                  }}
                                  style={{
                                    width: 20, height: 20, borderRadius: '50%',
                                    border: 'none', background: 'rgba(239,68,68,0.15)', color: '#fb7185',
                                    fontSize: 13, cursor: 'pointer', padding: 0,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  }}
                                  title="Remove pose"
                                >
                                  &#x2715;
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : result.poses && result.poses.length > 0 ? (
                  <div style={{ color: 'var(--text-muted)', marginBottom: 16, fontSize: 13 }}>
                    All poses have been removed.
                  </div>
                ) : (
                  <div style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
                    <pre style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--hover-bg)', padding: 12, borderRadius: 8, overflow: 'auto' }}>
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button onClick={() => {
                    const exportData = { ...result, poses: activePoses.map(({ pose }) => pose) };
                    navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
                  }} style={btnStyle}>Copy Results</button>
                  <button onClick={() => {
                    const exportData = { ...result, poses: activePoses.map(({ pose }) => pose) };
                    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'docking_results.json'; a.click(); URL.revokeObjectURL(url);
                  }} style={btnStyle}>Download JSON</button>
                  <button onClick={() => {
                    const lines = ['pose,affinity_kcal_mol'];
                    activePoses.forEach(({ pose }, i) => {
                      lines.push(`DOCK-${String(i + 1).padStart(3, '0')},${pose.affinity != null ? pose.affinity.toFixed(2) : ''}`);
                    });
                    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'docking_results.csv'; a.click(); URL.revokeObjectURL(url);
                  }} style={btnStyle}>Download CSV</button>
                  <button onClick={() => setSaveOpen(true)} style={btnStyle}>Save to Project</button>
                </div>
                <SaveResultsWizard isOpen={saveOpen} onClose={() => setSaveOpen(false)} rawData={{ ...result, poses: activePoses.map(({ pose }) => pose) }} toolName="Docking" toolKey="dock" smiles={ctxSmiles || ligandText} moleculeId={ctxMoleculeId} fileId={ctxFileId} projectId={ctxProjectId} />
              </div>
            )}
          </div>

          <div style={{ width: 320, flexShrink: 0 }}>
            <MethodCard engine="Vina" title="AutoDock Vina 1.2"
              description="Molecular docking with an empirical scoring function that sums five energy terms: van der Waals (Lennard-Jones 12-6), hydrogen bonding (directional H-bond), electrostatic (Coulombic), desolvation (hydrophobic contact), and torsional penalty (entropy cost of freezing rotatable bonds). Uses iterated local search for global optimisation."
              equation="ΔG = w_vdw·f_vdw + w_hbond·f_hbond + w_elec·f_elec + w_desolv·f_desolv + w_tors·N_tors"
              parameters={[
                { name: 'exhaustiveness', desc: 'Number of independent MC+local-search runs (higher = slower, more accurate)' },
                { name: 'num_modes', desc: 'Max number of binding poses to return' },
                { name: 'affinity', desc: 'Predicted binding free energy (kcal/mol, more negative = stronger)' },
              ]}
              reference="Trott & Olson (2010) J Comput Chem 31:455-461"
              referenceUrl="https://doi.org/10.1002/jcc.21334"
              source="AutoDock Vina empirical scoring function" />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

const warningBannerStyle: React.CSSProperties = { background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#fbbf24', lineHeight: 1.5 };
const statusBarStyle: React.CSSProperties = { padding: '8px 20px', background: 'var(--hover-bg)', borderBottom: '1px solid var(--card-border)', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 };
const h2Style: React.CSSProperties = { color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 };
const descStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 };
const btnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const primaryBtnStyle: React.CSSProperties = { ...btnStyle, background: '#f59e0b', color: 'var(--content-bg)', border: '1px solid #f59e0b', fontWeight: 600 };
const textareaStyle: React.CSSProperties = { width: '100%', padding: 14, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace', resize: 'vertical', outline: 'none', boxSizing: 'border-box' };
const selectStyle: React.CSSProperties = { padding: '4px 8px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13 };
const errorStyle: React.CSSProperties = { marginTop: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fb7185', fontSize: 13 };
const spinnerStyle: React.CSSProperties = { width: 20, height: 20, border: '3px solid var(--card-border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' };
const thStyle: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', color: '#a7abb6', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle: React.CSSProperties = { padding: '6px 12px', color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: 13 };

export default WorkbenchDocking;
